/**
 * Preferred products — per-parameter default commercial product selections.
 *
 * Synchronous API backed by a module cache. Persisted on the Pi
 * (namespace `preferred-products`, `Record<parameter, productId>`); the
 * legacy localStorage copy (`reefapp:preferred-products`) is the
 * instant-on fallback and is kept in sync on writes (never deleted).
 * The cache hydrates from the Pi on first use (async, best-effort);
 * writes update cache + localStorage and fire-and-forget a PUT.
 */
import { userDataApi } from '../lib/userdata-api';

/** Legacy browser-only key — kept in sync, never deleted. */
const LEGACY_STORAGE_KEY = 'reefapp:preferred-products';

/** `/api/userdata` namespace holding the selections on the Pi. */
export const PREFERRED_PRODUCTS_NAMESPACE = 'preferred-products';

let cache: Record<string, string> | null = null;
let hydrationStarted = false;

function isStringRecord(value: unknown): value is Record<string, string> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  return Object.values(value).every((v) => typeof v === 'string');
}

function readLegacy(): Record<string, string> {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (isStringRecord(parsed)) return parsed;
  } catch {
    /* noop */
  }
  return {};
}

function writeLegacy(record: Record<string, string>): void {
  try {
    localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* noop */
  }
}

/**
 * Kick off the one-time async hydration from the Pi. Local writes made
 * while the request is in flight win over the server copy (they are
 * newer and were themselves PUT to the server).
 */
function startHydration(): void {
  if (hydrationStarted) return;
  hydrationStarted = true;
  void userDataApi.get(PREFERRED_PRODUCTS_NAMESPACE).then((res) => {
    if (res.ok && isStringRecord(res.value.data)) {
      cache = { ...res.value.data, ...(cache ?? {}) };
      writeLegacy(cache);
    }
  });
}

/** Test seam: forget the cache/hydration state. */
export function resetPreferredProductsCacheForTests(): void {
  cache = null;
  hydrationStarted = false;
}

export function getPreferredProducts(): Record<string, string> {
  startHydration();
  return { ...(cache ?? readLegacy()) };
}

export function setPreferredProduct(parameter: string, productId: string): void {
  startHydration();
  const next = { ...(cache ?? readLegacy()), [parameter]: productId };
  cache = next;
  writeLegacy(next);
  // Fire-and-forget Pi write — failures are logged, never thrown.
  void userDataApi.put(PREFERRED_PRODUCTS_NAMESPACE, next).then((res) => {
    if (!res.ok) {
      console.info('[preferred-products] non-fatal Pi write error:', res.error);
    }
  });
}

export function getPreferredProductForParameter(parameter: string): string | null {
  return getPreferredProducts()[parameter] ?? null;
}
